import {
    LastMileConfigResponseSchema,
    type LastMileConfigResponseType,
    type CustomerSettingsType,
    CustomerSettingsReadonlySchema,
    defaultCustomerSettingsReadonly,
    type CustomerSettingsReadonlyType,
} from './settings';
import { CustomerSettingsSchema } from './settings';
import { z } from 'zod';
import { Api } from '@automapa/svelte-automapa-template';

export const CustomerSettingsResponseSchema = z.object({
    settings: CustomerSettingsSchema,
    settings_readonly: CustomerSettingsReadonlySchema,
    jobs_filter_config: LastMileConfigResponseSchema.nullable().optional(),
    revision: z.string().catch(''),
    id_property: z.string().nullable().optional(),
    webuser_name: z.string().catch(''),
});
export type CustomerSettingsResponseType = z.infer<typeof CustomerSettingsResponseSchema>;

// export const SettingsRequestSchema = z.array(z.object({
//     op: z.literal('replace'),
//     path: z.literal('/settings'),
//     value: CustomerSettingsSchema
// }));
// export type SettingsRequestType = z.infer<typeof SettingsRequestSchema>;

export const AccountRequestSchema = z.object({
    name: z.string().catch(''),
    email: z.string().catch(''),
    expiration_date: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/i)
        .nullable()
        .catch(null),
    webuser_id: z.number().optional(),
    customer_id: z.number().optional(),
    is_expired: z.boolean().nullable().catch(null),
    settings_readonly: CustomerSettingsReadonlySchema.catch(defaultCustomerSettingsReadonly),
});
export type AccountRequestType = z.infer<typeof AccountRequestSchema>;

/**
 * Lista kont w panelu admina. Flagi uprawnień dokłada Settings::getAccountSql() (US-018) i są
 * opcjonalne świadomie: ta sama definicja opisuje żądanie createAccount, które ich nie wysyła,
 * a starszy backend bez tych kolumn ma dawać konto bez przełącznika, nie błąd parsowania.
 */
export const AccountResponseSchema = AccountRequestSchema.extend({
    can_read: z.boolean().optional(),
    can_write: z.boolean().optional(),
    is_user_manager: z.boolean().optional(),
});
export type AccountResponseType = z.infer<typeof AccountResponseSchema>;

/** Żądanie i odpowiedź Admin::setUserManager() - backend zwraca stan konta po zmianie. */
export const SetUserManagerRequestSchema = z.object({
    webuser_id: z.number(),
    is_user_manager: z.boolean(),
});
export type SetUserManagerRequestType = z.infer<typeof SetUserManagerRequestSchema>;

export const SetUserManagerResponseSchema = z.object({
    webuser_id: z.number(),
    can_read: z.boolean(),
    can_write: z.boolean(),
    is_user_manager: z.boolean(),
});
export type SetUserManagerResponseType = z.infer<typeof SetUserManagerResponseSchema>;

export const AccountCreateResponseSchema = z.object({
    user_password: z.string(),
});
export type AccountCreateResponseType = z.infer<typeof AccountCreateResponseSchema>;

/**
 * Odpowiedź Settings::getMyPermissions() - backend zwraca snake_case. Bez .catch(), bo dla
 * uprawnień milczące domyślne wartości są gorsze niż błąd (patrz toPermissions w stores/app.ts).
 */
export const MyPermissionsResponseSchema = z.object({
    can_read: z.boolean(),
    can_write: z.boolean(),
    is_user_manager: z.boolean(),
    is_admin: z.boolean(),
});
export type MyPermissionsResponseType = z.infer<typeof MyPermissionsResponseSchema>;

export class SettingsApi {
    public static async getSettingsReadonly(api: Api): Promise<CustomerSettingsReadonlyType> {
        return api.getData<any, CustomerSettingsReadonlyType>(
            'post',
            `/my/settings/settings_readonly`,
            {},
            z.object({}),
            CustomerSettingsReadonlySchema
        );
    }

    public static async getMyPermissions(api: Api): Promise<MyPermissionsResponseType> {
        return api.getData<any, MyPermissionsResponseType>(
            'post',
            `/my/settings/get_my_permissions`,
            {},
            z.object({}),
            MyPermissionsResponseSchema
        );
    }

    public static async isAdmin(api: Api): Promise<boolean> {
        return api.getData<any, boolean>(
            'post',
            `/my/settings/is_admin`,
            {},
            z.object({}),
            z.boolean()
        );
    }

    public static async getCustomerSettings(
        api: Api,
        customerId: number | string = 'my'
    ): Promise<CustomerSettingsResponseType> {
        return api.getData<undefined, CustomerSettingsResponseType>(
            'get',
            '/config/' + customerId,
            undefined,
            z.undefined(),
            CustomerSettingsResponseSchema
        );
    }

    public static async setCustomerSettings(
        api: Api,
        customerSettings: CustomerSettingsType,
        customerId: number | string = 'my'
    ): Promise<CustomerSettingsResponseType> {
        return api.patch(
            '/config/' + customerId,
            'replace',
            '/settings',
            customerSettings,
            CustomerSettingsResponseSchema
        );
    }

    public static async getFilterConfig(api: Api, endpoint: string) {
        // POST /api/job/get_filter_config
        return api.getData<any, LastMileConfigResponseType>(
            'post',
            `/${endpoint}/get_filter_config`,
            {},
            z.object({}),
            LastMileConfigResponseSchema
        );

        // make curl for this:
        // POST /api/job/get_filter_config
        // curl -X POST 'https://lastmile-test.automapa.pl/api/job/get_filter_config' -H 'Token: 40eb651811ed1209139dbef2ff4c8a97' -H 'accept: application/json, text/plain, */*' -k
    }

    public static async setClientSettings<T>(
        api: Api,
        key: string,
        value: T,
        S: z.Schema
    ): Promise<any[]> {
        return api.getData<{ key: string; value: T }, any[]>(
            'post',
            `/my/settings/set`,
            { key, value },
            z.object({ key: z.string(), value: S }),
            z.array(z.any()).length(0)
        );
    }

    public static async getClientSettings<T>(api: Api, key: string, S: z.Schema): Promise<T> {
        return api.getData<{ key: string }, T>(
            'post',
            `/my/settings/get`,
            { key },
            z.object({ key: z.string() }),
            S
        );
    }

    public static async resetData<T>(api: Api): Promise<T> {
        return api.getData<any, T>(
            'post',
            `/my/settings/reset_data`,
            {},
            z.object({}),
            z.array(z.any()).length(0)
        );
    }

    public static async createTemplate<T>(api: Api): Promise<T> {
        return api.getData<any, T>(
            'post',
            `/my/settings/create_template_account`,
            {},
            z.object({}),
            z.any()
        );
    }

    public static async getDemoAccounts__DEPRECATED(
        api: Api,
        onlyMyAccount: boolean = true
    ): Promise<AccountResponseType[]> {
        return api.getData<any, AccountResponseType[]>(
            'post',
            `/my/settings/get_account` + (onlyMyAccount ? '' : 's'),
            {},
            z.any(),
            z.array(AccountResponseSchema)
        );
    }
}
